import type { Conviction, PortfolioInput, Position, PositionRole } from './types.js';
import { POSITION_ROLES } from './types.js';
import { knownLeverageFactor } from './leverage.js';

export interface ParsedLine {
  ticker: string;
  shares: number | null;
  value: number | null;
  costBasis: number | null;
  raw: string;
}

export interface ParseImportResult {
  positions: ParsedLine[];
  errors: Array<{ line: number; raw: string; reason: string }>;
}

const TICKER_RE = /^[A-Z][A-Z0-9.\-]{0,9}$/;

/**
 * Paste-import: one position per line, `TICKER qty [costBasis]`.
 * Accepts comma, tab, semicolon or whitespace delimiters. A `$` prefix on the
 * quantity means dollar notional instead of share count.
 *
 *   AAPL 100
 *   NVDA, 250, 118.40
 *   MSFT  $25000
 */
export function parseImport(text: string): ParseImportResult {
  const positions: ParsedLine[] = [];
  const errors: ParseImportResult['errors'] = [];

  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const raw = (lines[i] ?? '').trim();
    if (raw === '' || raw.startsWith('#') || raw.startsWith('//')) continue;

    const fields = raw.split(/[,;\t]+|\s+/).filter((f) => f !== '');
    const ticker = (fields[0] ?? '').toUpperCase();

    if (!TICKER_RE.test(ticker)) {
      errors.push({ line: i + 1, raw, reason: `"${fields[0] ?? ''}" is not a valid ticker` });
      continue;
    }

    const qtyField = fields[1];
    if (qtyField === undefined) {
      errors.push({ line: i + 1, raw, reason: 'missing quantity' });
      continue;
    }

    const isNotional = qtyField.startsWith('$');
    const qty = parseNumber(qtyField);
    if (qty === null) {
      errors.push({ line: i + 1, raw, reason: `"${qtyField}" is not a number` });
      continue;
    }

    positions.push({
      ticker,
      shares: isNotional ? null : qty,
      value: isNotional ? qty : null,
      costBasis: fields[2] !== undefined ? parseNumber(fields[2]) : null,
      raw,
    });
  }

  return { positions, errors };
}

function parseNumber(field: string): number | null {
  const cleaned = field.replace(/[$,_]/g, '');
  if (cleaned === '' || !/^-?\d*\.?\d+$/.test(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export function isRole(x: unknown): x is PositionRole {
  return typeof x === 'string' && (POSITION_ROLES as string[]).includes(x);
}

export function toConviction(x: unknown): Conviction {
  const n = typeof x === 'number' ? Math.round(x) : Number.parseInt(String(x ?? ''), 10);
  if (!Number.isFinite(n)) return 3;
  return Math.min(5, Math.max(0, n)) as Conviction;
}

/**
 * Validate and normalize an untrusted POST body into positions the engines can
 * consume. Returns the errors rather than throwing so the API can report all of
 * them at once.
 */
export function normalizePortfolioInput(
  body: unknown,
  makeId: () => string,
): { input: PortfolioInput & { positions: Position[] }; errors: string[] } {
  const errors: string[] = [];
  const obj = (body ?? {}) as Record<string, unknown>;

  const name = typeof obj.name === 'string' && obj.name.trim() !== '' ? obj.name.trim() : 'Untitled portfolio';
  const rawPositions = Array.isArray(obj.positions) ? obj.positions : [];
  if (!Array.isArray(obj.positions)) errors.push('positions must be an array');

  const positions: Position[] = [];
  for (let i = 0; i < rawPositions.length; i++) {
    const p = (rawPositions[i] ?? {}) as Record<string, unknown>;
    const ticker = String(p.ticker ?? '').trim().toUpperCase();
    if (!TICKER_RE.test(ticker)) {
      errors.push(`position ${i + 1}: invalid ticker "${p.ticker ?? ''}"`);
      continue;
    }

    const shares = numberOrNull(p.shares);
    const value = numberOrNull(p.value);
    if (shares === null && value === null) {
      errors.push(`position ${i + 1} (${ticker}): needs either shares or value`);
      continue;
    }

    positions.push({
      id: typeof p.id === 'string' && p.id !== '' ? p.id : makeId(),
      ticker,
      shares,
      value,
      costBasis: numberOrNull(p.costBasis),
      role: isRole(p.role) ? p.role : 'core',
      conviction: toConviction(p.conviction),
      sectorOverride: typeof p.sectorOverride === 'string' && p.sectorOverride.trim() !== '' ? p.sectorOverride.trim() : null,
      // Default from the known-ETP registry so a user who pastes TQQQ gets 3x
      // without having to know to set it, but an explicit value always wins.
      leverageFactor: numberOrNull(p.leverageFactor) ?? knownLeverageFactor(ticker),
    });
  }

  const settings =
    obj.settings && typeof obj.settings === 'object' ? (obj.settings as PortfolioInput['settings']) : undefined;

  return {
    input: {
      ...(typeof obj.id === 'string' && obj.id !== '' ? { id: obj.id } : {}),
      name,
      ...(settings ? { settings } : {}),
      positions,
    },
    errors,
  };
}

function numberOrNull(x: unknown): number | null {
  if (x === null || x === undefined || x === '') return null;
  const n = typeof x === 'number' ? x : Number(String(x).replace(/[$,_]/g, ''));
  return Number.isFinite(n) ? n : null;
}
